// components/Header.js
import React, { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import logo from "../assets/logo-me-1.png";

const Header = ({ darkMode, toggleDarkMode }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      document.body.style.overflow = originalOverflow;
      window.removeEventListener("keydown", handleEscape);
    };
  }, [isMobileMenuOpen]);

  const navItems = [
    { to: "/", label: "Home" },
    { to: "/about", label: "About" },
    { to: "/pricing", label: "Pricing" },
    { to: "/pictorial", label: "Pictorial" },
    { to: "/contact", label: "Contact" },
  ];

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const getDesktopLinkClass = ({ isActive }) =>
    `relative text-[11px] font-semibold uppercase tracking-[0.22em] transition-colors group ${
      isActive
        ? "text-[var(--brand-blue)] dark:text-white"
        : "text-gray-600 hover:text-[var(--brand-blue)] dark:text-gray-400 dark:hover:text-white"
    }`;

  const getMobileLinkClass = ({ isActive }) =>
    `py-3 text-sm font-semibold uppercase tracking-[0.18em] transition-colors ${
      isActive
        ? "text-[var(--brand-blue)] dark:text-white"
        : "text-gray-600 hover:text-[var(--brand-blue)] dark:text-gray-400 dark:hover:text-white"
    }`;

  return (
    <header
      className={`fixed top-0 w-full z-50 transition-all duration-500 ${
        isScrolled
          ? "border-b border-black/10 bg-[#f4f3ee]/88 py-2 backdrop-blur-md dark:border-white/10 dark:bg-black/82"
          : "bg-transparent py-5"
      }`}
    >
      <div className="container mx-auto flex items-center justify-between px-6">
        <Link
          to="/"
          onClick={closeMobileMenu}
          className="text-xl font-bold"
          aria-label="Go to homepage"
        >
          <img
            src={logo}
            alt="William Odhiambo"
            className={`w-32 h-auto transition-all duration-500 ${
              isScrolled ? "h-10" : "h-12"
            } ${darkMode ? "filter invert brightness-0" : ""}`}
          />
        </Link>

        <nav className="hidden md:flex space-x-6 lg:space-x-8">
          {navItems.map((item, index) => (
            <NavLink key={index} to={item.to} className={getDesktopLinkClass}>
              {({ isActive }) => (
                <>
                  {item.label}
                  <span
                    className={`absolute -bottom-2 left-0 h-px bg-gradient-to-r from-[var(--brand-blue)] to-[var(--brand-green)] transition-all duration-300 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  ></span>
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center space-x-4">
          <button
            onClick={toggleDarkMode}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-transparent text-gray-900 transition-all duration-300 hover:border-black dark:border-white/15 dark:text-white dark:hover:border-white"
            aria-label="Toggle dark mode"
            type="button"
          >
            {darkMode ? (
              <i className="bx bx-sun text-xl"></i>
            ) : (
              <i className="bx bx-moon text-xl"></i>
            )}
          </button>

          <button
            className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 bg-transparent text-gray-900 transition-colors dark:border-white/15 dark:text-white md:hidden"
            onClick={() => setIsMobileMenuOpen((prev) => !prev)}
            aria-label="Toggle mobile menu"
            type="button"
          >
            <i
              className={`bx ${
                isMobileMenuOpen ? "bx-x" : "bx-menu"
              } text-xl`}
            ></i>
          </button>
        </div>
      </div>

      <div
        className={`fixed inset-0 z-40 bg-black/45 transition-opacity duration-300 md:hidden ${
          isMobileMenuOpen
            ? "pointer-events-auto opacity-100"
            : "pointer-events-none opacity-0"
        }`}
        onClick={closeMobileMenu}
        aria-hidden="true"
      />

      <aside
        className={`fixed right-0 top-0 z-50 flex h-screen w-[min(86vw,360px)] flex-col border-l border-black/10 bg-[#f4f3ee] px-6 py-6 transition-transform duration-500 dark:border-white/10 dark:bg-black md:hidden ${
          isMobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
        aria-label="Mobile navigation"
      >
        <div className="flex items-center justify-between">
          <Link to="/" onClick={closeMobileMenu} aria-label="Go to homepage">
            <img
              src={logo}
              alt="William Odhiambo"
              className={`h-auto w-28 ${darkMode ? "filter invert brightness-0" : ""}`}
            />
          </Link>

          <button
            className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-gray-900 transition-colors hover:border-[var(--brand-blue)] hover:text-[var(--brand-blue)] dark:border-white/15 dark:text-white"
            onClick={closeMobileMenu}
            aria-label="Close mobile menu"
            type="button"
          >
            <i className="bx bx-x text-xl"></i>
          </button>
        </div>

        <nav className="mt-16 flex flex-col gap-2">
          {navItems.map((item, index) => (
            <NavLink
              key={index}
              to={item.to}
              className={getMobileLinkClass}
              onClick={closeMobileMenu}
            >
              {String(index + 1).padStart(2, "0")} / {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto border-t border-black/10 pt-6 dark:border-white/10">
          <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-gray-500 dark:text-gray-400">
            William Writes Code
          </p>

          <div className="mt-4 flex gap-3">
            <a
              href="https://github.com/i-william17"
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-gray-900 transition hover:border-[var(--brand-green)] hover:text-[var(--brand-green)] dark:border-white/10 dark:text-white"
              aria-label="GitHub"
            >
              <i className="bx bxl-github text-xl"></i>
            </a>

            <a
              href="https://www.linkedin.com/in/william-odhiambo-481689265/"
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-gray-900 transition hover:border-[var(--brand-blue)] hover:text-[var(--brand-blue)] dark:border-white/10 dark:text-white"
              aria-label="LinkedIn"
            >
              <i className="bx bxl-linkedin text-xl"></i>
            </a>

            <a
              href="https://x.com/talesofwilliam"
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 w-10 items-center justify-center rounded-full border border-black/10 text-gray-900 transition hover:border-[var(--brand-blue-2)] hover:text-[var(--brand-blue-2)] dark:border-white/10 dark:text-white"
              aria-label="X/Twitter"
            >
              <i className="bx bxl-twitter text-xl"></i>
            </a>
          </div>
        </div>
      </aside>
    </header>
  );
};

export default Header;
